import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { AlertCircle, ArrowRight, Check, ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  formMappingKey,
  getMappedCountForKeys,
  getMappingValue,
  getOrphanedMappingKeys,
  normalizeQueryParamName,
  queryMappingKey,
  resolveMappingKey,
  SYSTEM_VISITOR_MAPPING_FIELDS,
  type VisitorFormField,
} from './visitorMappingFields';

type TargetRendererArgs = {
  mappingKey: string;
  value?: string;
  onSelect: (targetValue: string) => void;
};

type VisitorMappingSectionsProps = {
  formFields: VisitorFormField[];
  mappings: Record<string, string>;
  selectedKey: string | null;
  onSelectKey: (mappingKey: string) => void;
  onMapKey: (mappingKey: string, targetValue: string) => void;
  onClearKey: (mappingKey: string) => void;
  getTargetLabel: (targetValue: string) => string;
  renderTargetInput: (args: TargetRendererArgs) => ReactNode;
  targetLabel: string;
  queryTargetLabel?: string;
  className?: string;
};

export function VisitorMappingSections({
  formFields,
  mappings,
  selectedKey,
  onSelectKey,
  onMapKey,
  onClearKey,
  getTargetLabel,
  renderTargetInput,
  targetLabel,
  queryTargetLabel,
  className,
}: VisitorMappingSectionsProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [queryInput, setQueryInput] = useState('');
  const [queryKeys, setQueryKeys] = useState<string[]>([]);

  useEffect(() => {
    const mappedQueryKeys = Object.keys(mappings || {}).filter((key) => key.startsWith('query.'));
    setQueryKeys((prev) => Array.from(new Set([...prev, ...mappedQueryKeys])));
  }, [mappings]);

  const formRows = useMemo(() => {
    return (formFields || [])
      .filter((field) => typeof field?.name === 'string' && field.name.trim())
      .map((field) => ({
        key: formMappingKey(field.name),
        legacyKey: field.name,
        label: field.label || field.name,
        description: field.description,
      }));
  }, [formFields]);

  const systemRows = useMemo(() => {
    return SYSTEM_VISITOR_MAPPING_FIELDS.map((field) => ({
      key: field.key,
      legacyKey: field.legacyKey || field.name,
      label: field.label,
      description: field.description,
    }));
  }, []);

  const orphanedMappingKeys = useMemo(
    () => getOrphanedMappingKeys(mappings, formFields),
    [mappings, formFields],
  );

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleClear = (mappingKey: string, event: MouseEvent) => {
    event.stopPropagation();
    onClearKey(mappingKey);
    if (mappingKey.startsWith('query.')) {
      setQueryKeys((prev) => prev.filter((key) => key !== mappingKey));
    }
  };

  const handleAddQueryParam = () => {
    const normalized = normalizeQueryParamName(queryInput);
    if (!normalized) return;
    const nextKey = queryMappingKey(normalized);
    setQueryKeys((prev) => Array.from(new Set([...prev, nextKey])));
    setQueryInput('');
    onSelectKey(nextKey);
  };

  const renderMappingRow = (
    row: { key: string; legacyKey?: string; label: string; description?: string },
    iconFallback: string,
  ) => {
    const actualKey = resolveMappingKey(mappings, row.key, row.legacyKey);
    const mappedValue = mappings[actualKey];
    const isMapped = Boolean(mappedValue);
    const isSelected = selectedKey === actualKey || selectedKey === row.key;

    return (
      <div
        key={row.key}
        className={cn(
          'rounded-lg border transition-all duration-200',
          isSelected
            ? 'border-primary bg-primary/5 shadow-sm'
            : isMapped
              ? 'border-emerald-500/30 bg-emerald-500/10'
              : 'hover:border-muted-foreground/30',
        )}
      >
        <div
          className="p-3 cursor-pointer"
          onClick={() => onSelectKey(actualKey)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={cn(
                  "h-8 w-8 rounded-lg flex items-center justify-center text-xs font-medium flex-shrink-0",
                  isMapped
                    ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {isMapped ? <Check className="h-4 w-4" /> : iconFallback}
              </div>
              <div className="min-w-0">
                <span className="font-medium text-sm">{row.label}</span>
                {row.description ? (
                  <p className="text-xs text-muted-foreground">{row.description}</p>
                ) : null}
                {row.legacyKey && actualKey === row.legacyKey ? (
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                    Legacy key: {row.legacyKey}
                  </p>
                ) : null}
                {isMapped && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-emerald-600 dark:text-emerald-500 truncate">
                      {getTargetLabel(mappedValue)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {isMapped && (
              <Button
                variant="ghost"
                size="icon"
                onClick={(event) => handleClear(actualKey, event)}
                className="h-7 w-7 text-muted-foreground hover:text-destructive flex-shrink-0"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
        {isSelected && (
          <div className="px-3 pb-3 pt-1 border-t bg-muted/30">
            <label className="block text-xs font-medium text-muted-foreground mb-2">
              {targetLabel}
            </label>
            {renderTargetInput({
              mappingKey: actualKey,
              value: mappedValue,
              onSelect: (targetValue) => onMapKey(actualKey, targetValue),
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={cn("rounded-xl border bg-card overflow-hidden", className)}>
      {orphanedMappingKeys.length > 0 ? (
        <>
          <div className="p-5">
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="text-sm font-medium">Orphaned mappings</div>
                  <div className="text-xs text-muted-foreground">
                    These mappings reference fields that are no longer available. Remove them to prevent sync errors.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {orphanedMappingKeys.map((key) => (
                      <Badge key={key} variant="secondary" className="gap-1">
                        <span className="font-mono text-xs">{key}</span>
                        <button
                          type="button"
                          className="ml-1 rounded hover:bg-muted px-0.5"
                          onClick={() => onClearKey(key)}
                          aria-label={`Remove orphaned mapping ${key}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Separator />
        </>
      ) : null}

      <Collapsible
        open={expandedSections.form !== false}
        onOpenChange={() => toggleSection('form')}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            {expandedSections.form === false ? (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
            <h4 className="font-medium">Form Fields</h4>
            <Badge variant="secondary" className="text-[10px]">
              {formRows.length}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {getMappedCountForKeys(mappings, formRows)} mapped
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 space-y-2">
            {formRows.map((row) => renderMappingRow(row, row.label.charAt(0).toUpperCase()))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      <Collapsible
        open={expandedSections.system !== false}
        onOpenChange={() => toggleSection('system')}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            {expandedSections.system === false ? (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
            <h4 className="font-medium">System Fields</h4>
            <Badge variant="outline" className="text-[10px]">
              {systemRows.length}
            </Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {getMappedCountForKeys(mappings, systemRows)} mapped
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 space-y-2">
            <p className="text-xs text-muted-foreground mb-3">
              Automatically captured visitor profile data
            </p>
            {systemRows.map((row) => renderMappingRow(row, row.label.charAt(0).toUpperCase()))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      <Collapsible
        open={expandedSections.query !== false}
        onOpenChange={() => toggleSection('query')}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            {expandedSections.query === false ? (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
            <h4 className="font-medium">Query Parameters</h4>
            {queryKeys.length > 0 ? (
              <Badge variant="outline" className="text-[10px]">
                {queryKeys.length}
              </Badge>
            ) : null}
          </div>
          <span className="text-xs text-muted-foreground">
            {queryKeys.filter((key) => Boolean(getMappingValue(mappings, key))).length} mapped
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 space-y-3">
            <p className="text-xs text-muted-foreground">
              Type a URL query parameter, then map it to a {queryTargetLabel || targetLabel.toLowerCase()}.
            </p>
            <div className="flex gap-2">
              <Input
                value={queryInput}
                onChange={(event) => setQueryInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddQueryParam();
                  }
                }}
                placeholder="utm_source"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddQueryParam}
                disabled={!normalizeQueryParamName(queryInput)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
            {queryKeys.length > 0 ? (
              <div className="space-y-2">
                {queryKeys.map((key) => {
                  const paramName = key.replace(/^query\./, '');
                  return renderMappingRow(
                    {
                      key,
                      label: paramName,
                      description: `URL query parameter: ${paramName}`,
                    },
                    '?',
                  );
                })}
              </div>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
